import { format, parseISO } from 'date-fns'
import { type WeekStartsOn } from '@opallabs/concepts'
import { DatePicker } from '@opallabs/satellite'
import { orderedLiveRange } from '../liveRange'
import { type CalendarDateRangeFilter } from '../types'
import * as styles from './styles'

/** What a field says while its end of the window is still open. */
const ANY_DATE = 'Any date'

const DISPLAY_FORMAT = 'MMM d, yyyy'

type LiveRangeFieldProps = Readonly<{
  htmlId: string
  label: string
  onChange: (day: string | undefined) => void
  /** ISO 8601 at day precision, or nothing while this end is open. */
  value?: string
  weekStartsOn?: WeekStartsOn
}>

const LiveRangeField: React.FC<LiveRangeFieldProps> = ({
  htmlId,
  label,
  onChange,
  value,
  weekStartsOn,
}) => {
  const selected = value === undefined ? undefined : parseISO(value)

  return (
    <div className={styles.selectors.dateField}>
      <span className={styles.selectors.dateFieldLabel}>{label}</span>
      <DatePicker
        // The button carries the date it holds, so the label above it would
        // otherwise be the only thing a screenreader heard.
        ariaLabel={`${label}: ${
          selected === undefined ? ANY_DATE : format(selected, DISPLAY_FORMAT)
        }`}
        buttonVariant="tertiary"
        dateDisplayFormat={DISPLAY_FORMAT}
        datePickerDisplayType="button"
        htmlId={htmlId}
        onDateSelect={(day) => onChange(day?.dateString)}
        placeholder={ANY_DATE}
        selectedDate={selected}
        showButtonText={true}
        weekStartsOn={weekStartsOn}
      />
    </div>
  )
}
LiveRangeField.displayName = 'Calendar: LiveRangeField'

export type LiveRangeLeafProps = Readonly<{
  onChange: (range: CalendarDateRangeFilter) => void
  range: CalendarDateRangeFilter
  weekStartsOn?: WeekStartsOn
}>

/**
 * The days an item has to be live on, as the two ends of one window.
 *
 * Either end can be left open, which is why they are two fields rather than
 * one range picker. Either end can also be set first, so a date landing on the
 * wrong side of the other is read as the window it means rather than as an
 * empty one. There is no Include or Exclude here: a window says which days a
 * person is looking at, and hiding the items inside it would be a different
 * question.
 */
export const LiveRangeLeaf: React.FC<LiveRangeLeafProps> = ({
  onChange,
  range,
  weekStartsOn,
}) => {
  const set = (end: CalendarDateRangeFilter) =>
    onChange(orderedLiveRange({ ...range, ...end }))

  return (
    <div className={styles.selectors.leaf}>
      <div className={styles.selectors.dateFields}>
        <LiveRangeField
          htmlId="calendar-filter-live-from"
          label="Start"
          onChange={(from) => set({ from })}
          value={range.from}
          weekStartsOn={weekStartsOn}
        />
        <LiveRangeField
          htmlId="calendar-filter-live-to"
          label="End"
          onChange={(to) => set({ to })}
          value={range.to}
          weekStartsOn={weekStartsOn}
        />
      </div>
    </div>
  )
}
LiveRangeLeaf.displayName = 'Calendar: LiveRangeLeaf'
